/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { UserRoles } from '@constants/user';
import { CurrentUserData } from '../../reducers/current-user';
import { PermissionsUser } from './types';
import { hasPermission } from './utils';

export class InitiativePermissions {
  static canContributeRoles = [UserRoles.Contributor];
  static canVerifyRoles = [UserRoles.Verifier];
  static canManageRoles = [UserRoles.Owner, UserRoles.Manager];
  static canAccessInsightsAndDownloadsRoles = [
    UserRoles.Owner,
    UserRoles.Manager,
    UserRoles.Contributor,
    UserRoles.Verifier,
    UserRoles.ContributorVerifier,
    UserRoles.Viewer,
  ];

  public static isOwner(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, [UserRoles.Owner]);
  }

  public static canManageInitiative(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, InitiativePermissions.canManageRoles);
  }

  public static canManageCustomReports(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, [
      UserRoles.Owner,
      UserRoles.Manager,
      UserRoles.User,
      UserRoles.Contributor,
      UserRoles.Viewer,
    ]);
  }

  public static canAccessInsightsAndDownloads(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, this.canAccessInsightsAndDownloadsRoles);
  }

  public static canAccess(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, [
      UserRoles.Owner,
      UserRoles.Manager,
      UserRoles.User,
      UserRoles.Contributor,
      UserRoles.Verifier,
      UserRoles.Viewer,
    ]);
  }

  public static canAccessAllSurveyData(user: PermissionsUser, initiativeId: string) {
    return hasPermission(user, initiativeId, [
      UserRoles.Owner,
      UserRoles.Manager,
      UserRoles.Contributor,
      UserRoles.Verifier,
      UserRoles.Viewer,
    ]);
  }

  public static canContribute(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, this.canContributeRoles);
  }

  public static canVerify(user: PermissionsUser, initiativeId: string): boolean {
    return hasPermission(user, initiativeId, this.canVerifyRoles);
  }

  public static hasRoleToManage({ permissions }: Pick<CurrentUserData, 'permissions'>) {
    if (!permissions || permissions.length === 0) {
      return false;
    }

    return permissions.some((p) =>
      p.permissions.some((role) => {
        return InitiativePermissions.canManageRoles.includes(role);
      }),
    );
  }
}
